import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { activeIngredients, ingredientPillars } from '@/constants/home'

export const Ingredients = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-ingredients-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.04,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 74%' }
        })

        gsap.from(select('[data-ingredients-intro]'), {
            y: 26,
            opacity: 0,
            duration: 0.95,
            delay: 0.15,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 74%' }
        })

        // The flat lay unmasks from the bottom, then keeps drifting as you scroll.
        const media = select('[data-ingredients-media]')[0]

        if (media) {
            gsap.fromTo(
                media,
                { clipPath: 'inset(0% 0% 100% 0%)' },
                {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.5,
                    ease: 'nuvea',
                    scrollTrigger: { trigger: media, start: 'top 84%' }
                }
            )

            gsap.fromTo(
                media.querySelector('img'),
                { yPercent: -9 },
                {
                    yPercent: 9,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: media,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 0.8
                    }
                }
            )
        }

        // Ingredient rows tick in from the left, one per line.
        gsap.from(select('[data-ingredient-row]'), {
            xPercent: -4,
            opacity: 0,
            duration: 0.85,
            stagger: 0.08,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-ingredient-list]')[0],
                start: 'top 82%'
            }
        })

        gsap.from(select('[data-ingredient-pillar]'), {
            y: 40,
            opacity: 0,
            duration: 0.95,
            stagger: 0.1,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-ingredient-pillars]')[0],
                start: 'top 85%'
            }
        })
    })

    return (
        <section
            ref={scope}
            id='ingredients'
            className='shell-x mx-auto w-full max-w-[1760px] py-24 md:py-32'
        >
            <div className='grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16'>
                <SplitWords
                    data-ingredients-heading=''
                    as='h2'
                    lines={['A short list,', 'deliberately.']}
                    className='type-h1 max-w-[14ch] text-ink'
                />

                <p
                    data-ingredients-intro=''
                    className='type-body max-w-[54ch] text-slate-soft lg:self-end'
                >
                    Five actives, each at a dose the published research actually used. No botanical extract
                    added because it reads well on a label, no fragrance, nothing whose job
                    is to make the ingredient list longer.
                </p>
            </div>

            <div className='mt-14 grid grid-cols-1 gap-10 md:mt-20 lg:grid-cols-[1.05fr_1fr] lg:gap-16'>
                <div
                    data-ingredients-media=''
                    className='relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-shell lg:order-2'
                >
                    <Image
                        src='/images/ingredients-lay.png'
                        alt='The five Nuvéa ingredients laid out on a pale surface in soft daylight'
                        fill
                        sizes='(min-width: 1024px) 50vw, 100vw'
                        className='scale-110 object-cover will-change-transform'
                    />
                </div>

                <div className='flex flex-col justify-center lg:order-1'>
                    <p className='type-label text-slate-mute'>
                        The five
                    </p>

                    <ul data-ingredient-list='' className='mt-6 border-t border-stone-line'>
                        {activeIngredients.map((ingredient) => (
                            <li
                                key={ingredient.name}
                                data-ingredient-row=''
                                className='group flex items-baseline justify-between gap-6 border-b border-stone-line py-5 transition-[padding] duration-500 ease-nuvea hover:pl-2'
                            >
                                <h3 className='type-h3 text-ink'>
                                    {ingredient.name}
                                </h3>

                                <div className='flex shrink-0 flex-col items-end'>
                                    <span className='type-micro text-slate-mute'>
                                        {ingredient.index}
                                    </span>

                                    <span className='type-micro text-slate-soft opacity-0 transition-opacity duration-500 ease-nuvea group-hover:opacity-100'>
                                        {ingredient.role}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <ul
                data-ingredient-pillars=''
                className='mt-16 grid grid-cols-1 gap-8 md:mt-24 md:grid-cols-3 md:gap-6 lg:gap-12'
            >
                {ingredientPillars.map((pillar) => (
                    <li
                        key={pillar.number}
                        data-ingredient-pillar=''
                        className='rounded-2xl border border-stone-line bg-shell p-7 transition-[background-color,transform] duration-500 ease-nuvea hover:-translate-y-1 hover:bg-sage/40'
                    >
                        <div className='flex items-baseline justify-between gap-4'>
                            <h3 className='type-body text-ink'>
                                {pillar.title}
                            </h3>

                            <span className='type-small text-slate-mute tabular-nums'>
                                {pillar.number}
                            </span>
                        </div>

                        <p className='type-copy mt-4 max-w-[42ch] text-slate-soft'>
                            {pillar.body}
                        </p>
                    </li>
                ))}
            </ul>
        </section>
    )
}
